/**
 * 018: COPIED from
 *   vendor/isomorphic/apps/isomorphic/src/app/auth/(forgot-password)/forgot-password-1/page.tsx
 *   wrapper: vendor/isomorphic/apps/isomorphic/src/app/shared/auth-layout/auth-wrapper-one.tsx
 *   with the field markup of vendor/isomorphic/apps/isomorphic/src/app/auth/(sign-in)/sign-in-1/
 *   sign-in-form.tsx
 *
 * The wrapper is the template's own file, imported from @steelyard/theme, banner column and hero
 * image included — not `auth-wrapper-three`'s bare card. The field is a rizzui
 * `Input` with the template's own size, label and input classes, wrapped in the template's own
 * `Form` (@core/ui/form.tsx) — the same composition as sign-in.tsx in this folder.
 *
 * Only the data, labels, routes and business rules are ours.
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { SubmitHandler } from "react-hook-form";
import { Button, Input, Text } from "rizzui";
import {
  AuthWrapperOne,
  UnderlineShape,
  Form,
  FieldError,
  forgetPasswordSchema,
  type ForgetPasswordSchema,
} from "@steelyard/theme";
import { requestPasswordReset } from "../../lib/identity";
import { useTurnstile } from "../../components/turnstile";
import heroImage from "../../assets/auth/sign-in-hero.svg";

/**
 * Ported from apps/admin/app/(auth)/password-reset/page.tsx. The confirmation is a constant —
 * reads identically whether or not the address has an account, and the call is never branched on
 * either (anti-enumeration, both sides of the round trip).
 *
 * The email field's validation is the barrel's own `forgetPasswordSchema` ({ email: validateEmail
 * }) — the same rule sign-in.tsx's `loginSchema` uses for its own email field — not a hand-rolled
 * copy of it.
 */
const RESET_REQUESTED =
  "If that email address has an account, a reset link is on its way to it. The link expires, so use it soon.";
const RESET_LINK_REFUSED =
  "That reset link is no longer usable. Links expire and can only be used once — request another below.";
const RESET_LINK_THROTTLED = "Too many attempts — wait a few minutes and try again.";

function resetLinkRefusalText(status: string): string {
  return status === "429" ? RESET_LINK_THROTTLED : RESET_LINK_REFUSED;
}

export default function PasswordResetPage() {
  const [searchParams] = useSearchParams();
  const refused = searchParams.get("refused");
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const captcha = useTurnstile("password_reset");

  const onSubmit: SubmitHandler<ForgetPasswordSchema> = async (values) => {
    setSubmitError(null);
    try {
      await requestPasswordReset(values.email, await captcha.getToken());
      setSent(true);
    } catch (err) {
      // The problem's own words: a failed security check says to complete it again.
      setSubmitError(err);
    } finally {
      captcha.reset();
    }
  };

  return (
    <AuthWrapperOne
      title={
        <>
          Reset your{" "}
          <span className="relative inline-block">
            password.
            <UnderlineShape className="absolute -bottom-2 end-0 h-2.5 w-28 text-blue xl:-bottom-1.5 xl:w-36" />
          </span>
        </>
      }
      bannerTitle="Infra Junction's operations, in one back office."
      bannerDescription="Orders, credit, GST compliance and warehouse dispatch — the same system your colleagues use, kept current with what the API actually returns."
      isSocialLoginActive={false}
      pageImage={
        <div className="relative mx-auto aspect-[4/3.37] h-[calc(100vh-31rem)] max-w-full">
          <img src={heroImage} alt="" className="absolute inset-0 size-full rounded-lg object-contain" />
        </div>
      }
    >
      <Text className="-mt-4 mb-6 text-center leading-loose text-gray-500 lg:text-start">
        We will email you a link. Your current password keeps working until you use it.
      </Text>

      {refused ? (
        <div className="mb-5">
          <FieldError message={resetLinkRefusalText(refused)} />
        </div>
      ) : null}
      {submitError ? (
        <div className="mb-5">
          <FieldError error={submitError} />
        </div>
      ) : null}
      {sent ? (
        <Text role="status" className="text-sm">
          {RESET_REQUESTED}
        </Text>
      ) : null}

      <Form<ForgetPasswordSchema>
        validationSchema={forgetPasswordSchema}
        onSubmit={onSubmit}
        useFormProps={{ defaultValues: { email: "" } }}
      >
        {({ register, formState: { errors, isSubmitting } }) => (
          <div className="space-y-5">
            <Input size="lg" className="[&>label>span]:font-medium" inputClassName="text-sm" label="Email" type="email" autoFocus autoComplete="username" inputMode="email" maxLength={320} error={errors.email?.message} {...register("email")} />
            {captcha.widget}
            <Button type="submit" className="w-full" size="lg" isLoading={isSubmitting}>
              Send the link
            </Button>
          </div>
        )}
      </Form>

      <Text className="mt-6 text-center leading-loose text-gray-500 lg:mt-8 lg:text-start">
        <Link to="/sign-in" className="font-semibold text-gray-700 transition-colors hover:text-blue">Back to sign in</Link>
      </Text>
    </AuthWrapperOne>
  );
}
